import React, { ReactNode, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Row, Spacer } from "../../../components/Layout/Layout";
import Icon from "../Icon";
import Button, { ButtonType } from "../Button/Button";
import {
  breakpoints,
  useWindowDimensions,
} from "../../../core/hooks/dimensionProvider";

import styles from "./PrimeModal.module.css";

const FOCUSABLE_SELECTOR = [
  'a[href]:not([disabled]):not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(", ");

interface FooterButton {
  text: string;
  onClick?: () => Promise<void> | void;
  variant?: "primary" | "destructive" | "secondary";
  disabled?: boolean;
  image?: string;
  hidden?: boolean;
  type?: ButtonType;
  form?: string;
  loading?: boolean;
}

interface IPrimeModalProps {
  children: ReactNode;
  isOpen: boolean;
  onClose(): void;
  withHeader?: boolean;
  header?: string | ReactNode;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  modal?: boolean;
  withFooter?: boolean;
  submitBtn?: FooterButton;
  cancelBtn?: FooterButton;
  loading?: boolean;
  customFooter?: ReactNode;
  className?: string;
  contentClassName?: string;
  position?:
    | "center"
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";
  dataCy?: string;
  fixedWidth?: string;
}

const PrimeModal = ({
  children,
  isOpen,
  onClose,
  withHeader,
  header,
  withFooter,
  style,
  contentStyle,
  modal = true,
  submitBtn,
  cancelBtn,
  loading,
  customFooter,
  className,
  contentClassName,
  position,
  dataCy,
  fixedWidth,
}: Readonly<IPrimeModalProps>) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= breakpoints.desktop;
  const dialogRef = useRef<Dialog>(null);

  useEffect(() => {
    if (!isOpen) return;

    // Custom focus trap — only active for the topmost modal
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const dialogEl = dialogRef.current?.getElement?.();
      if (!dialogEl) return;

      const allMasks = document.querySelectorAll(".p-dialog-mask");
      const topMask = allMasks[allMasks.length - 1];
      if (!topMask?.contains(dialogEl)) return;

      e.preventDefault();
      e.stopPropagation();

      const focusable = Array.from(
        dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null);

      if (!focusable.length) return;

      const activeIndex = focusable.indexOf(
        document.activeElement as HTMLElement
      );
      let nextIndex: number;

      if (e.shiftKey) {
        nextIndex = activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1;
      } else {
        nextIndex =
          activeIndex === -1 || activeIndex >= focusable.length - 1
            ? 0
            : activeIndex + 1;
      }

      focusable[nextIndex].focus();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen]);

  const renderFooter = () => {
    if (customFooter)
      return (
        <Row className="modal-footer-wrapper" type="left">
          {customFooter}
        </Row>
      );
    if (withFooter) {
      return (
        <Row className="modal-footer-wrapper" type="left">
          {submitBtn && !submitBtn.hidden && (
            <Button
              dataCy="modal_submit_btn"
              type={submitBtn.type}
              form={submitBtn.form}
              text={submitBtn.text}
              image={submitBtn.image}
              variant={submitBtn.variant ?? "primary"}
              onClick={submitBtn.onClick}
              disabled={loading || submitBtn.disabled}
              loading={loading ?? submitBtn.loading}
            />
          )}
          {cancelBtn && (
            <>
              {submitBtn && !submitBtn.hidden && <Spacer size={8} />}
              <Button
                dataCy="modal_cancel_btn"
                text={cancelBtn.text}
                variant={cancelBtn.variant ?? "secondary"}
                onClick={cancelBtn.onClick}
                disabled={loading}
                image={cancelBtn.image}
              />
            </>
          )}
        </Row>
      );
    }
  };

  return (
    <Dialog
      ref={dialogRef}
      blockScroll
      closeOnEscape={false}
      draggable={false}
      visible={isOpen}
      onHide={onClose}
      showHeader={withHeader}
      header={header}
      modal={modal}
      contentStyle={{ padding: 0 }}
      contentClassName={contentClassName}
      style={{
        ...(fixedWidth && isDesktop && { maxWidth: fixedWidth, width: "100%" }),
        ...style,
      }}
      resizable={false}
      footer={renderFooter()}
      closeIcon={<Icon name="x" />}
      className={className}
      position={position}
      data-cy={dataCy}
      pt={{ header: { "data-cy": `${dataCy}_header` } }}
    >
      <div className={styles.primeWrapper} style={contentStyle}>
        {children}
      </div>
    </Dialog>
  );
};

export default PrimeModal;
